import React from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

/**
 * Brand Button — variants: primary | secondary | ghost | link
 * Sizes: sm | md | lg
 * Renders either as <a> (if href) or <button>. Framer Motion hover/tap.
 */
const variantClasses = {
  primary:
    "bg-brand-emerald text-black hover:bg-brand-emeraldDark hover:shadow-emerald-glow-soft border border-brand-emerald/60",
  secondary:
    "bg-white/[0.03] text-white border border-white/12 hover:bg-white/[0.06] hover:border-white/20",
  ghost:
    "bg-transparent text-white border border-transparent hover:bg-white/[0.05]",
  link:
    "bg-transparent text-white underline-offset-4 hover:text-brand-emerald hover:underline p-0 h-auto",
};

const sizeClasses = {
  sm: "h-9 px-4 text-sm rounded-full gap-1.5",
  md: "h-11 px-5 text-[15px] rounded-full gap-2",
  lg: "h-12 px-6 text-base rounded-full gap-2",
};

export const Button = React.forwardRef(function Button(
  {
    as,
    href,
    variant = "primary",
    size = "md",
    leftIcon,
    rightIcon,
    className,
    children,
    disabled,
    type = "button",
    "data-testid": testId,
    ...props
  },
  ref,
) {
  const Comp = href ? motion.a : motion.button;
  const commonProps = {
    ref,
    className: cn(
      "inline-flex items-center justify-center font-medium transition-colors",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-emerald/60 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base",
      "disabled:cursor-not-allowed disabled:opacity-50",
      variant !== "link" && sizeClasses[size],
      variantClasses[variant],
      className,
    ),
    whileHover: disabled ? undefined : { scale: 1.02 },
    whileTap: disabled ? undefined : { scale: 0.97 },
    transition: { type: "spring", stiffness: 400, damping: 22 },
    "data-testid": testId,
  };

  if (href) {
    return (
      <Comp href={href} {...commonProps} {...props}>
        {leftIcon && <span className="inline-flex">{leftIcon}</span>}
        {children}
        {rightIcon && <span className="inline-flex">{rightIcon}</span>}
      </Comp>
    );
  }

  return (
    <Comp type={type} disabled={disabled} {...commonProps} {...props}>
      {leftIcon && <span className="inline-flex">{leftIcon}</span>}
      {children}
      {rightIcon && <span className="inline-flex">{rightIcon}</span>}
    </Comp>
  );
});

export default Button;
